import { useEffect, useState } from 'react';
import { cn } from '../lib/cn';

/**
 * Rolling per-visitor urgency countdown.
 *
 * The visitor's FIRST visit is anchored in localStorage; the timer counts
 * `periodHours` down to 0 and then restarts — `period - (elapsed % period)`
 * rolls forever, so the anchor is written exactly once. Distinct from the
 * storefront `Countdown`, which targets a fixed instant and stops at zero.
 *
 * Lives in @mlm/ui because there are now two callers — the page-level timer
 * banner (member app) and the `countdown` section (both apps) — and a second
 * copy of "when does this visitor's clock start" would drift the same way the
 * old duplicated preview renderer did.
 */
export function useRollingCountdown(periodHours: number, storageKey: string): number | null {
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    const periodMs = Math.max(1, periodHours) * 3600_000;
    let anchor = Number(localStorage.getItem(storageKey));
    // A future anchor means a clock change or a tampered value — re-anchor
    // rather than showing a countdown that never moves.
    if (!Number.isFinite(anchor) || anchor <= 0 || anchor > Date.now()) {
      anchor = Date.now();
      try {
        localStorage.setItem(storageKey, String(anchor));
      } catch {
        /* private mode — a session-only timer is fine */
      }
    }
    const tick = () => setRemaining(periodMs - ((Date.now() - anchor) % periodMs));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [periodHours, storageKey]);

  return remaining;
}

function parts(remaining: number) {
  const total = Math.floor(remaining / 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    hh: pad(Math.floor(total / 3600)),
    mm: pad(Math.floor((total % 3600) / 60)),
    ss: pad(total % 60),
  };
}

/**
 * The compact banner form. This is the exact markup the page-level timer has
 * always rendered — don't restyle it without checking the live pages.
 */
export function RollingCountdown({
  periodHours,
  label,
  storageKey,
}: {
  periodHours: number;
  label: string;
  storageKey: string;
}) {
  const remaining = useRollingCountdown(periodHours, storageKey);
  if (remaining == null) return null;
  const { hh, mm, ss } = parts(remaining);
  return (
    <div className="flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-primary-foreground">
      <span className="text-sm font-medium">{label}</span>
      <span className="flex items-center gap-1 font-mono text-base font-bold tabular-nums">
        <span className="rounded bg-black/20 px-1.5 py-0.5">{hh}</span>:
        <span className="rounded bg-black/20 px-1.5 py-0.5">{mm}</span>:
        <span className="rounded bg-black/20 px-1.5 py-0.5">{ss}</span>
      </span>
    </div>
  );
}

/** The large form used by the `countdown` SECTION. */
export function RpCountdownBlocks({
  periodHours,
  label,
  storageKey,
  className,
}: {
  periodHours: number;
  label: string;
  storageKey: string;
  className?: string;
}) {
  const remaining = useRollingCountdown(periodHours, storageKey);
  if (remaining == null) return null;
  const { hh, mm, ss } = parts(remaining);
  return (
    <div className={cn('space-y-3', className)}>
      <p className="text-sm font-medium uppercase tracking-widest opacity-80">{label}</p>
      <div className="flex items-center justify-center gap-3">
        {[
          { v: hh, l: 'Hours' },
          { v: mm, l: 'Minutes' },
          { v: ss, l: 'Seconds' },
        ].map((p) => (
          <div key={p.l} className="min-w-[72px] rounded-lg bg-black/10 px-3 py-2">
            <span className="block font-mono text-3xl font-bold tabular-nums">{p.v}</span>
            <span className="block text-[10px] uppercase tracking-wide opacity-70">{p.l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
